import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { SentryService } from '@rd-lib/sentry/sentry.service';
import { bootstrapResponseSchema } from '@rd/schemas/bootstrap.schema';
import { loyaltyCurrencySerializer } from '@rd/serializers/loyalty-currency.serializer';
import { transferRateSerializer } from '@rd/serializers/transfer-rate.serializer';
import {
  BootstrapData,
  Currency,
  getCapability,
  Locale,
  LoyaltyCurrency,
  LoyaltyProgram,
} from '@rd/types';
import { AutoRedemptionRule } from '@rd/types/auto-redemption-rule.type';
import { AutoRedemptionSettingStrategy } from '@rd/types/auto-redemption-setting.type';
import { BootstrapResponse } from '@rd/types/bootstrap.response';
import {
  LoyaltyProgramCapabilities,
  LoyaltyProgramCapability,
} from '@rd/types/loyalty-program-capability.type';
import { RewardsProgram } from '@rd/types/rewards_program.type';
import { TransferRate } from '@rd/types/transfer-rate.type';
import { isValidUrl } from '@rd/utils/url.utils';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiValidationService } from '../api-validation/api-validation.service';
import { NydusNetworkApiDataService } from '../nydus-network-api-data/nydus-network-api-data.service';

@Injectable({
  providedIn: 'root',
})
export class BootstrapApiService {
  private apiValidationService = inject(ApiValidationService);
  private nydusNetworkApiDataService = inject(NydusNetworkApiDataService);
  private sentryService = inject(SentryService);

  getBootstrap(): Observable<BootstrapData> {
    return this.nydusNetworkApiDataService
      .get<BootstrapResponse>('bootstrap', new HttpParams())
      .pipe(
        map((res) => {
          this.apiValidationService.validate<BootstrapResponse>({
            apiName: 'bootstrap',
            schema: bootstrapResponseSchema,
            response: res,
          });
          return this.transformBootstrapResponse(res);
        }),
      );
  }

  private transformBootstrapResponse(
    bootstrapResponse: BootstrapResponse,
  ): BootstrapData {
    const currencies: Currency[] = [];
    const locales: Locale[] = [];
    const loyaltyCurrencies: LoyaltyCurrency[] = [];
    const loyaltyPrograms: LoyaltyProgram[] = [];

    let rewardsProgram: RewardsProgram = {} as RewardsProgram;
    const timeZoneOffset = bootstrapResponse.data.attributes.timeZoneOffset;
    const roundingPreferences =
      bootstrapResponse.data.attributes.roundingPreferences;
    const paymentProcessingFee =
      bootstrapResponse.data.attributes.paymentProcessingFee;
    const minPointsPercentage =
      bootstrapResponse.data.attributes.minPointsPercentage;
    const maxPointsPercentage =
      bootstrapResponse.data.attributes.maxPointsPercentage;
    const featureFlags = bootstrapResponse.data.attributes.featureFlags;

    const autoRedemptionRules: AutoRedemptionRule[] =
      bootstrapResponse.data.attributes.autoRedemptionRules?.map((rule) => ({
        enabled: rule.attributes.enabled,
        productSku: rule.attributes.productSku,
        strategies: rule.attributes
          .strategies as AutoRedemptionSettingStrategy[],
      })) ?? []; // This is to take into account that this is optional in NN
    const timeZoneOffsetInMinutes =
      bootstrapResponse.data.attributes.timeZoneOffsetInMinutes;

    const capabilitiesMap: Map<string, LoyaltyProgramCapability> = new Map();
    const loyaltyCurrencyMap: Map<string, LoyaltyCurrency> = new Map();

    const rewardsLoyaltyMap: Map<string, LoyaltyProgram> = new Map();
    const transferRateMap: Map<string, TransferRate> = new Map(); // Transfer Rate <-> Loyalty Currency

    bootstrapResponse.included.forEach((data) => {
      switch (data.type) {
        case 'capability': {
          const capability = {
            id: data.id,
            ...data.attributes,
          };
          const capabilityUrls = [
            'detailsUrl',
            'conditionsUrl',
            'logoUrl',
            'enrollmentUrl',
          ];
          capabilityUrls.forEach((url: string) => {
            // TODO: fix later
            // isValidUrl already make data has correct type
            // @ts-ignore
            capability[url] = isValidUrl(data.attributes[url])
              ? // @ts-ignore
                new URL(data.attributes[url])
              : null;
          });
          capabilitiesMap.set(data.id, capability);
          break;
        }
        case 'currency':
          currencies.push({
            id: data.id,
            ...data.attributes,
          });
          break;
        case 'locale':
          locales.push({
            id: data.id,
            ...data.attributes,
          });
          break;
        case 'loyalty_currency':
          const loyaltyCurrency = loyaltyCurrencySerializer(data);
          loyaltyCurrencyMap.set(data.id, loyaltyCurrency);
          loyaltyCurrencies.push(loyaltyCurrency);
          break;
        case 'loyalty_program':
          const loyaltyProgram: Partial<LoyaltyProgram> = {
            id: data.id,
            ...data.attributes,
          };

          if (
            data.relationships.loyaltyCurrency &&
            loyaltyCurrencyMap.has(data.relationships.loyaltyCurrency.data.id)
          ) {
            loyaltyProgram.loyaltyCurrency = {
              ...loyaltyCurrencyMap.get(
                data.relationships.loyaltyCurrency.data.id,
              ),
            } as LoyaltyCurrency;
          }

          if (data.relationships.capabilities) {
            const loyaltyProgramCapabilities: Partial<LoyaltyProgramCapability>[] =
              data.relationships.capabilities.data.map((capability) => ({
                id: capability.id,
              }));

            // Casting this here first, so we can hydrate all capabilities later
            loyaltyProgram.capabilities =
              loyaltyProgramCapabilities as LoyaltyProgramCapability[];
          }

          if (data.relationships.rewardsProgram?.data) {
            rewardsLoyaltyMap.set(
              data.relationships.rewardsProgram.data.id,
              loyaltyProgram as LoyaltyProgram,
            );
          }

          loyaltyPrograms.push(loyaltyProgram as LoyaltyProgram);
          break;
        case 'rewards_program':
          rewardsProgram = {
            id: data.id,
            description: data.attributes.description,
            externalLink: data.attributes.externalLink,
            data: data.attributes.data,
            loyaltyProgram: null,
            loyaltyProgramId: '',
          };
          break;

        case 'points_transfer_rate':
        case 'cash_transfer_rate':
          const transferRate: TransferRate = transferRateSerializer(data);
          transferRateMap.set(data.attributes.loyaltyCurrencyId, transferRate);
          break;
      }
    });

    // Hydrate Capabilities
    for (const loyaltyProgram of loyaltyPrograms) {
      const fullCapabilities = [];
      if (loyaltyProgram.capabilities !== null) {
        for (const capability of loyaltyProgram.capabilities) {
          const fullCapability = capabilitiesMap.get(capability.id);
          fullCapabilities.push(fullCapability);
        }
      }

      loyaltyProgram.pointsTransferCapability = getCapability(
        fullCapabilities as LoyaltyProgramCapability[],
        LoyaltyProgramCapabilities.PointsTransfer,
        this.sentryService,
      );

      loyaltyProgram.benefitCapability = getCapability(
        fullCapabilities as LoyaltyProgramCapability[],
        LoyaltyProgramCapabilities.Benefit,
        this.sentryService,
      );

      loyaltyProgram.cashBackToCardCapability = getCapability(
        fullCapabilities as LoyaltyProgramCapability[],
        LoyaltyProgramCapabilities.CashBackToCard,
        this.sentryService,
      );

      loyaltyProgram.cashBackToAccountCapability = getCapability(
        fullCapabilities as LoyaltyProgramCapability[],
        LoyaltyProgramCapabilities.CashBackToAccount,
        this.sentryService,
      );

      loyaltyProgram.cashBackToPlatformCapability = getCapability(
        fullCapabilities as LoyaltyProgramCapability[],
        LoyaltyProgramCapabilities.CashBackToPlatform,
        this.sentryService,
      );
    }

    // Hydrate TransferRate
    for (const loyaltyProgram of loyaltyPrograms) {
      const loyaltyProgramCurrencyId = loyaltyProgram.loyaltyCurrency?.id;
      loyaltyProgram.transferRate = transferRateMap.get(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        loyaltyProgramCurrencyId!,
      )!;
    }

    // Add a check here to be able to support
    // Backwards compatibility where NN hasn't deployed
    // the rewards program yet
    if (rewardsLoyaltyMap.size > 0) {
      rewardsProgram.loyaltyProgram =
        rewardsLoyaltyMap.get(rewardsProgram.id) || null;
      rewardsProgram.loyaltyProgramId =
        rewardsLoyaltyMap.get(rewardsProgram.id)?.id || '';
    }

    return {
      currencies,
      locales,
      loyaltyCurrencies,
      loyaltyPrograms,
      rewardsProgram,
      timeZoneOffset,
      timeZoneOffsetInMinutes,
      autoRedemptionRules,
      roundingPreferences,
      paymentProcessingFee,
      minPointsPercentage,
      maxPointsPercentage,
      featureFlags,
    };
  }
}
